import * as MyKeysApi from './index';
import { CriteriaListDto, PageRequest, DatesRequest } from './interfaces';
import * as Devices from '../../../modules/devices';
import * as Download from '../../../modules/dialogs/download';
import {
  DownloadableFileDefault,
  MyKeysPromise,
  IDownloadableFile,
  IDownloadableCheck,
} from '../../../interfaces';
import * as DownloadLogs from '../../../modules/dialogs/downloadLogs';

const csvBaseFile: IDownloadableFile = {
  ...DownloadableFileDefault,
  filename: 'DownloadedDevices.csv',
};
export const downloadCSV = (
  criteria: CriteriaListDto,
  devices: Devices.PKey[],
  deviceLabels: string[]
): MyKeysPromise<IDownloadableFile> => {
  const url = 'downloads/devices/csv';
  const body = { criteria, devices, deviceLabels };
  return MyKeysApi.downloadFile(url, body, csvBaseFile);
};

const getOptions = (withoutMeters: boolean) =>
  withoutMeters
    ? Download.OptionsDto.DownloadOthers
    : Download.OptionsDto.DownloadMeters;

const xmlMeterBaseFile: IDownloadableFile = {
  ...DownloadableFileDefault,
  filename: 'DownloadedMeters.xml',
};
const xmlOtherBaseFile: IDownloadableFile = {
  ...DownloadableFileDefault,
  filename: 'DownloadedOtherDevices.xml',
};
export const downloadXML = (
  criteria: CriteriaListDto,
  devices: Devices.PKey[],
  deviceLabels: string[],
  withoutMeters: boolean
): MyKeysPromise<IDownloadableFile> => {
  const url = 'downloads/devices/xml';
  const fileBase = withoutMeters ? xmlOtherBaseFile : xmlMeterBaseFile;
  const options = getOptions(withoutMeters);
  const body = { criteria, devices, deviceLabels, options };
  return MyKeysApi.downloadFile(url, body, fileBase);
};

const omsMeterBaseFile: IDownloadableFile = {
  ...DownloadableFileDefault,
  filename: 'DownloadedMeters.oms',
};
const omsOtherBaseFile: IDownloadableFile = {
  ...DownloadableFileDefault,
  filename: 'DownloadedOtherDevices.oms',
};
export const downloadOMS = (
  criteria: CriteriaListDto,
  devices: Devices.PKey[],
  deviceLabels: string[],
  withoutMeters: boolean
): MyKeysPromise<IDownloadableFile> => {
  const url = 'downloads/devices/oms';
  const fileBase = withoutMeters ? omsOtherBaseFile : omsMeterBaseFile;
  const options = getOptions(withoutMeters);
  const body = { criteria, devices, deviceLabels, options };
  return MyKeysApi.downloadFile(url, body, fileBase);
};

const kemMeterBaseFile: IDownloadableFile = {
  ...DownloadableFileDefault,
  filename: 'DownloadedMeters.kem',
};
const kemOtherBaseFile: IDownloadableFile = {
  ...DownloadableFileDefault,
  filename: 'DownloadedOtherDevices.kem',
};
export const downloadKEM = (
  criteria: CriteriaListDto,
  devices: Devices.PKey[],
  deviceLabels: string[],
  withoutMeters: boolean,
  password: string
): MyKeysPromise<IDownloadableFile> => {
  const url = 'downloads/devices/kem';
  const fileBase = withoutMeters ? kemOtherBaseFile : kemMeterBaseFile;
  const options = getOptions(withoutMeters);
  const body = { criteria, devices, deviceLabels, options, password };
  return MyKeysApi.downloadFile(url, body, fileBase);
};

export const verifyKEM = (
  criteria: CriteriaListDto,
  devices: Devices.PKey[],
  deviceLabels: string[],
  withoutMeters: boolean,
  password: string
): MyKeysPromise<IDownloadableCheck> => {
  const url = 'downloads/Devices/ValidateKem';
  const options = getOptions(withoutMeters);
  const body = { criteria, devices, deviceLabels, options, password };
  return MyKeysApi.post(url, body, true)
    .then((res) => MyKeysApi.Map.mapDownloadCheckDto(res.data))    
    .catch(MyKeysApi.parseError);
};

const kem2BaseFile: IDownloadableFile = {
  ...DownloadableFileDefault,
  filename: 'DownloadedDevices.kem2',
};
export const downloadKEM2 = (
  criteria: CriteriaListDto,
  devices: Devices.PKey[],
  deviceLabels: string[],
  password: string
): MyKeysPromise<IDownloadableFile> => {
  const url = 'downloads/devices/kem2';
  const fileBase = kem2BaseFile;
  const options = Download.OptionsDto.DownloadAll;
  const body = { criteria, devices, deviceLabels, options, password };
  return MyKeysApi.downloadFile(url, body, fileBase);
};

export const verifyKEM2 = (
  criteria: CriteriaListDto,
  devices: Devices.PKey[],
  deviceLabels: string[],
  password: string
): MyKeysPromise<IDownloadableCheck> => {
  const url = 'downloads/Devices/ValidateKem2';
  const options = Download.OptionsDto.DownloadAll;
  const body = { criteria, devices, deviceLabels, options, password };
  return MyKeysApi.post(url, body, true)
    .then((res) => MyKeysApi.Map.mapDownloadCheckDto(res.data))    
    .catch(MyKeysApi.parseError);
};


const sigFoxBaseFile: IDownloadableFile = {
  ...DownloadableFileDefault,
  filename: 'DownloadedDevices.sigfox',
};
export const downloadSigFox = (
  criteria: CriteriaListDto,
  devices: Devices.PKey[],
  deviceLabels: string[]
): MyKeysPromise<IDownloadableFile> => {
  const url = 'downloads/devices/sigfox';
  const body = { criteria, devices, deviceLabels };
  return MyKeysApi.downloadFile(url, body, sigFoxBaseFile);
};

export const getDownloadLogs = (
  from?: string,
  to?: string
): MyKeysPromise<DownloadLogs.DownloadHistory> => {
  const pageReq: PageRequest = {
    pageIndex: 0,
    pageSize: DownloadLogs.HistoryPageSize,
  };
  const dateReq: DatesRequest = { from, to };
  const body = { ...pageReq, ...dateReq };
  return MyKeysApi.post('downloads/customer/history', body, true)
    .then((res) => MyKeysApi.Map.mapDownloadHistoryDto(res.data))
    .catch(MyKeysApi.parseError);
};
